import { AccountAllocation } from '@/components/Claim/AccountAllocation'
import { EmbeddedWalletClaim } from '@/components/Claim/EmbeddedWalletClaim'
import { EOAClaim } from '@/components/Claim/EOAClaim'
import { SmartWalletClaim } from '@/components/Claim/SmartWalletClaim'
import { COUNTER_DURATION, SPRING_TRANSITION } from '@/constants/animations'
import { useAccountAllocationsData } from '@/hooks/useAccountAllocationsData'
import { useWallets } from '@/hooks/useWallets'
import { formatNumber } from '@ourzora/utils'
import { motion } from 'framer-motion'
import { memo, useMemo } from 'react'
import { Text, View } from 'reshaped'
import { useCountUp } from 'use-count-up'
import { Address } from 'viem'
import { AnimatedNumber } from './AnimatedNumber'

export const ClaimAmount = memo(function ClaimAmount() {
  const { wallets: connectedWallets } = useWallets()

  const accounts = useAccountAllocationsData({
    identifiers: connectedWallets.map(([address]) => address),
  })

  const totalClaimable = useMemo(() => {
    return Object.values(accounts).reduce(
      (sum, account) => sum + (account.totalTokens || 0),
      0
    )
  }, [accounts])

  const isDustClaim = totalClaimable < 1

  const { value: animatedValue = 0 } = useCountUp({
    isCounting: !isDustClaim,
    end: totalClaimable,
    duration: COUNTER_DURATION,
  })

  // Extract UAPI account external wallet from top level query data
  const externalWalletAddress = useMemo(() => {
    return Object.values(accounts).reduce(
      (acc, account) => {
        if (account.externalWallet?.uapiAccountExternalWallet) {
          acc = account.externalWallet.uapiAccountExternalWallet
        }
        return acc
      },
      undefined as Address | undefined
    )
  }, [accounts])

  const formattedValue = formatNumber(Math.floor(Number(animatedValue) || 0))

  // Count total wallets (claimed + unclaimed) that have tokens
  const totalWallets = useMemo(() => {
    return Object.values(accounts).reduce((count, account) => {
      let walletCount = 0
      if (account.externalWallet?.tokens && account.externalWallet.tokens > 0)
        walletCount++
      if (account.embeddedWallet?.tokens && account.embeddedWallet.tokens > 0)
        walletCount++
      if (account.smartWallet?.tokens && account.smartWallet.tokens > 0) walletCount++
      return count + walletCount
    }, 0)
  }, [accounts])

  const eligibleWallets = useMemo(() => {
    return Object.entries(accounts).flatMap(([id, account]) => {
      const wallets = []

      if (
        account.externalWallet?.tokens &&
        account.externalWallet.tokens > 0 &&
        account.externalWallet.claimStatus !== 'CLAIMED'
      ) {
        wallets.push({
          id,
          type: 'external',
          account,
          tokens: account.externalWallet.tokens,
          address: account.externalWallet.walletAddress,
        })
      }

      if (
        account.embeddedWallet?.tokens &&
        account.embeddedWallet.tokens > 0 &&
        account.embeddedWallet.claimStatus !== 'CLAIMED'
      ) {
        wallets.push({
          id,
          type: 'embedded',
          account,
          tokens: account.embeddedWallet.tokens,
          address: account.embeddedWallet.walletAddress,
        })
      }

      if (
        account.smartWallet?.tokens &&
        account.smartWallet.tokens > 0 &&
        account.smartWallet.claimStatus !== 'CLAIMED'
      ) {
        wallets.push({
          id,
          type: 'smart',
          account,
          tokens: account.smartWallet.tokens,
          address: account.smartWallet.walletAddress,
        })
      }

      return wallets
    })
  }, [accounts])

  // Show individual wallets if there are multiple wallets total (claimed + unclaimed)
  const shouldShowIndividualWallets = totalWallets > 1

  const hasEligibleExternalWallet = useMemo(() => {
    return eligibleWallets.some((wallet) => wallet.type === 'external')
  }, [eligibleWallets])

  const eligibleWallet = eligibleWallets[0]

  return (
    <View direction="column" align="center" padding={4} gap={2} width="100%">
      <View direction="column" align="center" gap={6} width="100%">
        <Text
          variant={{ s: 'body-2', m: 'featured-2' }}
          weight="regular"
          color="neutral-faded"
        >
          Total $ZORA Claimable
        </Text>
        <View
          position="relative"
          align="center"
          justify="center"
          height={{ s: '120px', m: '200px' }}
          width={{ s: '100%', m: '925px' }}
        >
          <AnimatedNumber
            number={isDustClaim ? '< 1' : formattedValue}
            totalClaimableLength={String(Math.round(totalClaimable)).length}
          />
        </View>
      </View>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          ...SPRING_TRANSITION,
          delay: COUNTER_DURATION,
        }}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <View direction="column" align="center">
          <View
            direction="column"
            align="center"
            gap={6}
            paddingTop={8}
            width="100%"
            maxWidth="500px"
          >
            {shouldShowIndividualWallets ? (
              <>
                <View width="100%">
                  {Object.entries(accounts).map(([id, account]) => (
                    <AccountAllocation
                      key={id}
                      account={account}
                      externalWalletAddress={externalWalletAddress}
                    />
                  ))}
                </View>
              </>
            ) : (
              <View align="center" gap={6} width={{ s: '300px', m: '363px' }}>
                {eligibleWallet && (
                  <>
                    {eligibleWallet.type === 'external' && (
                      <EOAClaim
                        address={eligibleWallet.address}
                        status={
                          eligibleWallet.account.externalWallet?.claimStatus ||
                          'NOT_CLAIMED'
                        }
                        tokens={eligibleWallet.tokens}
                        width={{ s: '100%', m: '363px' }}
                        size="large"
                      />
                    )}
                    {eligibleWallet.type === 'embedded' && (
                      <EmbeddedWalletClaim
                        externalWalletAddress={externalWalletAddress}
                        address={eligibleWallet.address}
                        status={
                          eligibleWallet.account.embeddedWallet?.claimStatus ||
                          'NOT_CLAIMED'
                        }
                        tokens={eligibleWallet.tokens}
                        smartWalletAddress={
                          eligibleWallet.account.smartWallet?.walletAddress
                        }
                        width={{ s: '100%', m: '363px' }}
                        size="large"
                      />
                    )}
                    {eligibleWallet.type === 'smart' && (
                      <SmartWalletClaim
                        smartWalletAddress={eligibleWallet.address}
                        status={
                          eligibleWallet.account.smartWallet?.claimStatus || 'NOT_CLAIMED'
                        }
                        tokens={eligibleWallet.tokens}
                        smartWalletOwners={
                          eligibleWallet.account.smartWallet?.owners || []
                        }
                        width={{ s: '100%', m: '363px' }}
                        size="large"
                      />
                    )}
                  </>
                )}
              </View>
            )}
          </View>
          {hasEligibleExternalWallet && (
            <View paddingTop={!shouldShowIndividualWallets ? 4 : undefined}>
              <Text variant="body-2" color="neutral-faded">
                <a
                  href="https://support.zora.co/en/articles/5630465"
                  target="_blank"
                  rel="noreferrer"
                >
                  You'll need some Base ETH to claim
                </a>
              </Text>
            </View>
          )}
        </View>
      </motion.div>
    </View>
  )
})
